import React from 'react';
import { api } from '@/services/api';
import { noteInput, startActivityTracking } from '@/services/activity';
import { Button, Modal } from '@/components/kit';

/**
 * F9 session time limits: warns before the server signs this person out (backend app/auth/session_limits.py).
 *
 * - Idle: about two minutes before the idle limit, "Stay signed in" restarts the idle clock. Another tab's activity
 *   counts too: the warning asks the server for the time left before it shows, and hides when that moves on.
 * - Absolute: two minutes before the absolute limit, a notice to save work (it can't be extended).
 *
 * When time runs out the next request comes back 401 and the app goes to the sign-in page, which says why.
 */
export interface SessionTimeLeft {
  idle_seconds_left: number | null;
  absolute_seconds_left: number | null;
  idle_minutes: number;
  absolute_hours: number;
  warning_seconds: number;
}

export const CHECK_MS = 15_000;

interface Known {
  idleDeadline: number | null;       // when the server will sign out for idleness, as of the last check
  absoluteDeadline: number | null;
  warnMs: number;
}

const toKnown = (d: SessionTimeLeft, at: number): Known => ({
  idleDeadline: d.idle_seconds_left == null ? null : at + d.idle_seconds_left * 1000,
  absoluteDeadline: d.absolute_seconds_left == null ? null : at + d.absolute_seconds_left * 1000,
  warnMs: (d.warning_seconds || 120) * 1000,
});

/** m:ss */
export const formatLeft = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

type Showing = { kind: 'idle' | 'absolute'; deadline: number } | null;

const SessionTimeoutWarning: React.FC = () => {
  const known = React.useRef<Known | null>(null);
  const absoluteSeen = React.useRef(false);
  const [showing, setShowing] = React.useState<Showing>(null);
  const [now, setNow] = React.useState(() => Date.now());

  const decide = React.useCallback(() => {
    const k = known.current;
    if (!k) return;
    const t = Date.now();
    const show = (kind: 'idle' | 'absolute', deadline: number) =>
      setShowing(s => (s?.kind === kind && Math.abs(s.deadline - deadline) < 2000 ? s : { kind, deadline }));
    if (k.idleDeadline != null && k.idleDeadline - t <= k.warnMs) {
      show('idle', k.idleDeadline);
    } else if (k.absoluteDeadline != null && k.absoluteDeadline - t <= k.warnMs && !absoluteSeen.current) {
      show('absolute', k.absoluteDeadline);
    } else {
      setShowing(s => (s?.kind === 'absolute' ? s : null));
    }
  }, []);

  const check = React.useCallback(async () => {
    try {
      const r = await api.get<SessionTimeLeft>('/auth/session');
      known.current = toKnown(r.data, Date.now());
      decide();
    } catch {
      /* a 401 sends the app to the sign-in page (services/api.ts); anything else: try again next time */
    }
  }, [decide]);

  const showingRef = React.useRef<Showing>(null);
  showingRef.current = showing;

  React.useEffect(() => {
    startActivityTracking();
    check();
    let failedTicks = 0;
    const id = window.setInterval(() => {
      const k = known.current;
      if (!k) {                    // the first check failed: try again about once a minute, not every tick
        failedTicks += 1;
        if (failedTicks % 4 === 0) check();
        return;
      }
      const t = Date.now();
      // Quiet until a deadline the server last gave is close (the last WARNING + CHECK seconds, about 2¼ minutes
      // of the hour): then ask, as it may have moved on (this tab's input since, which the request reports, or
      // another tab's), or it may be time to warn. While the warning shows, keep asking so another tab's activity
      // closes it. Nothing for the absolute limit once its notice has been acknowledged.
      const near = (d: number | null) => d != null && d - t <= k.warnMs + CHECK_MS;
      if (near(k.idleDeadline) || (near(k.absoluteDeadline) && !absoluteSeen.current) || showingRef.current) check();
    }, CHECK_MS);
    return () => window.clearInterval(id);
  }, [check]);

  // The countdown, and the next check when it reaches 0 (the server then signs out: a 401)
  React.useEffect(() => {
    if (!showing) return;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= showing.deadline) { window.clearInterval(id); check(); }
    }, 1000);
    return () => window.clearInterval(id);
  }, [showing, check]);

  const staySignedIn = async () => {
    noteInput();
    try {
      const r = await api.post<SessionTimeLeft>('/auth/session/keep-alive');
      known.current = toKnown(r.data, Date.now());
    } catch { /* a 401: already signed out */ }
    setShowing(null);
  };

  const dismissAbsolute = () => { absoluteSeen.current = true; setShowing(null); };

  if (!showing) return null;
  const left = formatLeft(showing.deadline - now);
  if (showing.kind === 'idle') {
    return (
      <Modal open onClose={staySignedIn} title="Are you still there?" size="sm" testId="session-idle-warning"
             footer={<Button variant="primary" onClick={staySignedIn}>Stay signed in</Button>}>
        <p>Nothing has been done in MatchPoint for a while, so you&apos;ll be signed out in{' '}
          <strong data-testid="session-time-left">{left}</strong>. Anything you haven&apos;t saved will be lost.</p>
      </Modal>
    );
  }
  return (
    <Modal open onClose={dismissAbsolute} title="Your sign-in is about to end" size="sm" testId="session-absolute-warning"
           footer={<Button variant="primary" onClick={dismissAbsolute}>OK</Button>}>
      <p>Your sign-in reaches its time limit in <strong data-testid="session-time-left">{left}</strong>. Save your
        work now; you&apos;ll need to sign in again to carry on.</p>
    </Modal>
  );
};

export default SessionTimeoutWarning;
